import { Button, Divider, message, Table, Tabs } from 'antd';
import { Formik } from 'formik';
import { Form, Input, InputNumber, Select, SubmitButton } from 'formik-antd';
import { useEffect, useState } from 'react';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';
import {
  useGetGeneralConfigQuery,
  useGetGeneralDataConfigQuery,
  useGetLksbGoodMetricCriteriaConfigQuery,
  useUpsertGeneralConfigMutation,
} from './generalInfoAPI';
import { GeneralConfig, GeneralReportData, LksbGoodMetricCriteriaMap, LksbGoodMetricCriteriaType } from './types';

const { Option } = Select;
const { TabPane } = Tabs;
const basicConfigKey = 'config_general-basic_config';
const generalDataConfigKey = 'config_general-data';
const lksbGoodMetricConfigKey = 'config_lksb-good_metric_criteria';

const lksbMetricOptions = [
  { key: 'p1', label: 'P1 - 151 / NPL lebih dari 1 tahun' },
  { key: 'p2', label: 'P2 - (151 - NPL > 1 tahun) / NPL < 1 tahun' },
  { key: 'p5', label: 'P5 - Akumulasi Recovery / Total Charge Off' },
  { key: 'p6', label: 'P6 - Aset bersih terhadap simpanan+saham' },
  { key: 'e1', label: 'E1 - Pinjaman beredar / Total Aktiva Lancar' },
  { key: 'e2', label: 'E2 - (100+119+120) / Total Aktiva Lancar' },
  { key: 'e3', label: 'E3 - (102 + Total Penyertaan) / Total Aktiva Lancar' },
  { key: 'e5', label: 'E5 - Simpanan non-saham / Total Aktiva Lancar' },
  { key: 'e6', label: 'E6 - (446+447+487+480+481) / Total Aktiva Lancar' },
  { key: 'e7', label: 'E7 - (500+510) / Total Aktiva Lancar' },
  { key: 'e9', label: 'E9 - Modal lembaga bersih / Total Aktiva Lancar' },
  { key: 'a1', label: 'A1 - NPL / 150' },
  { key: 'a2', label: 'A2 - (Total Aktiva Tetap + NPL) / Total Aktiva' },
  { key: 'a3', label: 'A3 - (492+491) / (Total Aktiva Tetap + NPL)' },
  { key: 'r1', label: 'R1 - 600 / rata-rata (150+151) 2 tahun' },
  { key: 'r9', label: 'R9 - Pendapatan operasional / rata-rata Total Aktiva 2 tahun' },
  { key: 'r10', label: 'R10 - 748 / rata-rata Total Aktiva 2 tahun' },
  { key: 'l1', label: 'L1 - Likuiditas bersih terhadap simpanan non-saham' },
  { key: 'l3', label: 'L3 - 100 / Total Aktiva' },
  { key: 's1', label: 'S1 - Pertumbuhan (150+151) YoY' },
  { key: 's5', label: 'S5 - Total simpanan non-saham' },
  { key: 's6', label: 'S6 - Total akun 446+447+487+480+481' },
  { key: 's7', label: 'S7 - Total saham 500+510' },
  { key: 's9', label: 'S9 - (540..552) - 151 - NPL' },
  { key: 's11', label: 'S11 - Total Aktiva' },
];

const goodMetricTypeLabelMap: Record<LksbGoodMetricCriteriaType, string> = {
  GREATER_THAN: 'Lebih besar dari x (>x)',
  GREATER_THAN_OR_EQUAL: 'Lebih besar atau sama dengan x (>=x)',
  LESS_THAN: 'Lebih kecil dari x (<x)',
  LESS_THAN_OR_EQUAL: 'Lebih kecil atau sama dengan x (<=x)',
  GREATER_THAN_AND_LESS_THAN: 'Antara x dan y (x<nilai<y)',
  GREATER_THAN_OR_EQUAL_AND_LESS_THAN: 'Antara x dan y (x<=nilai<y)',
  GREATER_THAN_AND_LESS_THAN_OR_EQUAL: 'Antara x dan y (x<nilai<=y)',
  GREATER_THAN_OR_EQUAL_AND_LESS_THAN_OR_EQUAL: 'Antara x dan y (x<=nilai<=y)',
};

const metricLabelByKey = lksbMetricOptions.reduce(
  (acc, item) => {
    acc[item.key] = item.label;
    return acc;
  },
  {} as Record<string, string>,
);

type GoodMetricCriteriaForm = {
  metricKey: string;
  type: LksbGoodMetricCriteriaType;
  value?: number;
  min?: number;
  max?: number;
  descriptionOverride?: string;
};

const monthLabelMap: Record<number, string> = {
  1: 'Januari',
  2: 'Februari',
  3: 'Maret',
  4: 'April',
  5: 'Mei',
  6: 'Juni',
  7: 'Juli',
  8: 'Agustus',
  9: 'September',
  10: 'Oktober',
  11: 'November',
  12: 'Desember',
};

const normalizeGeneralReportData = (value: GeneralReportData[] | GeneralReportData | null | undefined): GeneralReportData[] => {
  if (!value) return [];
  const rawData = Array.isArray(value) ? value : [value];

  return rawData.map((item) => ({
    month: item.month,
    year: item.year,
    nplLessThanOneYearAmount: item.nplLessThanOneYearAmount ?? item.nplAmount ?? 0,
    nplMoreThanOneYearAmount: item.nplMoreThanOneYearAmount ?? 0,
    chargedOffLoanAmount: item.chargedOffLoanAmount ?? 0,
    accumulatedRecoveryAmount: item.accumulatedRecoveryAmount ?? 0,
  }));
};

const normalizeLksbGoodMetricCriteriaMap = (value: LksbGoodMetricCriteriaMap | null | undefined): LksbGoodMetricCriteriaMap => {
  if (!value || Array.isArray(value) || typeof value !== 'object') {
    return {};
  }

  const normalized: LksbGoodMetricCriteriaMap = {};
  Object.keys(value).forEach((key) => {
    const item = value[key];
    if (!item || typeof item !== 'object') {
      return;
    }

    const descriptionOverride = `${item.descriptionOverride ?? ''}`.trim() || undefined;

    // Single value threshold types
    if (item.type === 'GREATER_THAN' || item.type === 'GREATER_THAN_OR_EQUAL' || item.type === 'LESS_THAN' || item.type === 'LESS_THAN_OR_EQUAL') {
      const threshold = Number(item.value ?? item.min);
      if (Number.isFinite(threshold)) {
        normalized[key] = {
          type: item.type,
          value: threshold,
          descriptionOverride,
        };
      }
      return;
    }

    // Range threshold types
    if (
      item.type === 'GREATER_THAN_AND_LESS_THAN' ||
      item.type === 'GREATER_THAN_OR_EQUAL_AND_LESS_THAN' ||
      item.type === 'GREATER_THAN_AND_LESS_THAN_OR_EQUAL' ||
      item.type === 'GREATER_THAN_OR_EQUAL_AND_LESS_THAN_OR_EQUAL'
    ) {
      const min = Number(item.min);
      const max = Number(item.max);
      if (Number.isFinite(min) && Number.isFinite(max) && min < max) {
        normalized[key] = {
          type: item.type,
          min,
          max,
          descriptionOverride,
        };
      }
    }
  });

  return normalized;
};

function GeneralInfoConfig() {
  const [initialData, setInitialData] = useState<GeneralConfig>({
    countEntrepreneur: 0,
  });

  const [initialGeneralData, setInitialGeneralData] = useState<GeneralReportData>({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    nplLessThanOneYearAmount: 0,
    nplMoreThanOneYearAmount: 0,
    chargedOffLoanAmount: 0,
    accumulatedRecoveryAmount: 0,
  });

  const [generalReportDataList, setGeneralReportDataList] = useState<GeneralReportData[]>([]);
  const [goodMetricCriteriaMap, setGoodMetricCriteriaMap] = useState<LksbGoodMetricCriteriaMap>({});

  const {
    data: basicConfig,
    isFetching: isFetchingBasicConfig,
    isError: isErrorBasicConfig,
  } = useGetGeneralConfigQuery(basicConfigKey || '', {
    skip: basicConfigKey ? false : true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const {
    data: generalDataConfig,
    isFetching: isFetchingGeneralDataConfig,
    isError: isErrorGeneralDataConfig,
  } = useGetGeneralDataConfigQuery(generalDataConfigKey || '', {
    skip: generalDataConfigKey ? false : true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const {
    data: lksbGoodMetricConfig,
    isFetching: isFetchingGoodMetricConfig,
    isError: isErrorGoodMetricConfig,
  } = useGetLksbGoodMetricCriteriaConfigQuery(lksbGoodMetricConfigKey || '', {
    skip: lksbGoodMetricConfigKey ? false : true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [upsert] = useUpsertGeneralConfigMutation();

  useEffect(() => {
    if (basicConfig) {
      setInitialData({ countEntrepreneur: basicConfig.countEntrepreneur });
    }
  }, [basicConfig]);

  useEffect(() => {
    if (generalDataConfig) {
      const normalizedData = normalizeGeneralReportData(generalDataConfig);
      setGeneralReportDataList(normalizedData);

      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const currentPeriodData = normalizedData.find((item) => item.month === currentMonth && item.year === currentYear);

      setInitialGeneralData({
        month: currentMonth,
        year: currentYear,
        nplLessThanOneYearAmount: currentPeriodData?.nplLessThanOneYearAmount || 0,
        nplMoreThanOneYearAmount: currentPeriodData?.nplMoreThanOneYearAmount || 0,
        chargedOffLoanAmount: currentPeriodData?.chargedOffLoanAmount || 0,
        accumulatedRecoveryAmount: currentPeriodData?.accumulatedRecoveryAmount || 0,
      });
    }
  }, [generalDataConfig]);

  useEffect(() => {
    const normalizedCriteriaMap = normalizeLksbGoodMetricCriteriaMap(lksbGoodMetricConfig);
    setGoodMetricCriteriaMap(normalizedCriteriaMap);
  }, [lksbGoodMetricConfig]);

  const saveBasicConfig = async (value: GeneralConfig, closeLoading: any) => {
    upsert({
      key: basicConfigKey,
      value: JSON.stringify(value),
    })
      .then(() => {
        closeLoading();
        message.success('Berhasil!');
      })
      .catch(() => {
        closeLoading();
        message.error('Gagal!');
      });
  };

  const saveGeneralDataConfig = async (value: GeneralReportData, closeLoading: any) => {
    const currentData = [...generalReportDataList];
    const dataToSave = [...currentData];
    const selectedPeriodIndex = dataToSave.findIndex((item) => item.month === value.month && item.year === value.year);

    if (selectedPeriodIndex >= 0) {
      dataToSave.splice(selectedPeriodIndex, 1);
      dataToSave.push(value);
    } else {
      dataToSave.push(value);
    }

    upsert({
      key: generalDataConfigKey,
      value: JSON.stringify(dataToSave),
    })
      .then(() => {
        setGeneralReportDataList(dataToSave);
        closeLoading();
        message.success('Berhasil!');
      })
      .catch(() => {
        closeLoading();
        message.error('Gagal!');
      });
  };

  const saveGoodMetricCriteriaConfig = async (value: LksbGoodMetricCriteriaMap, closeLoading: any) => {
    upsert({
      key: lksbGoodMetricConfigKey,
      value: JSON.stringify(value),
    })
      .then(() => {
        setGoodMetricCriteriaMap(value);
        closeLoading();
        message.success('Berhasil!');
      })
      .catch(() => {
        closeLoading();
        message.error('Gagal!');
      });
  };

  const removeGoodMetricCriteria = async (metricKey: string) => {
    const hide = message.loading('Loading', 0);
    const updatedMap = { ...goodMetricCriteriaMap };
    delete updatedMap[metricKey];
    await saveGoodMetricCriteriaConfig(updatedMap, hide);
  };

  const isFetching = isFetchingBasicConfig || isFetchingGeneralDataConfig || isFetchingGoodMetricConfig;
  const isError = isErrorBasicConfig || isErrorGeneralDataConfig || isErrorGoodMetricConfig;

  const columns = [
    {
      title: 'Bulan',
      dataIndex: 'month',
      key: 'month',
      render: (month: number) => monthLabelMap[month] || month,
    },
    {
      title: 'Tahun',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'NPL < 1 Tahun (Rupiah)',
      dataIndex: 'nplLessThanOneYearAmount',
      key: 'nplLessThanOneYearAmount',
      render: (value: number) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`,
    },
    {
      title: 'NPL > 1 Tahun (Rupiah)',
      dataIndex: 'nplMoreThanOneYearAmount',
      key: 'nplMoreThanOneYearAmount',
      render: (value: number) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`,
    },
    {
      title: 'Charged Off Loan (Rupiah)',
      dataIndex: 'chargedOffLoanAmount',
      key: 'chargedOffLoanAmount',
      render: (value: number) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`,
    },
    {
      title: 'Akumulasi Recovery (Rupiah)',
      dataIndex: 'accumulatedRecoveryAmount',
      key: 'accumulatedRecoveryAmount',
      render: (value: number) => `Rp ${Number(value || 0).toLocaleString('id-ID')}`,
    },
  ];

  const goodMetricColumns = [
    {
      title: 'Metrik',
      dataIndex: 'metricKey',
      key: 'metricKey',
      render: (metricKey: string) => metricLabelByKey[metricKey] || metricKey,
    },
    {
      title: 'Kriteria',
      dataIndex: 'criteria',
      key: 'criteria',
      render: (criteria: any) => {
        if (!criteria) return '-';
        switch (criteria.type) {
          case 'GREATER_THAN':
            return `> ${Number(criteria.value).toLocaleString('id-ID')}`;
          case 'GREATER_THAN_OR_EQUAL':
            return `>= ${Number(criteria.value).toLocaleString('id-ID')}`;
          case 'LESS_THAN':
            return `< ${Number(criteria.value).toLocaleString('id-ID')}`;
          case 'LESS_THAN_OR_EQUAL':
            return `<= ${Number(criteria.value).toLocaleString('id-ID')}`;
          case 'GREATER_THAN_AND_LESS_THAN':
            return `> ${Number(criteria.min).toLocaleString('id-ID')} dan < ${Number(criteria.max).toLocaleString('id-ID')}`;
          case 'GREATER_THAN_OR_EQUAL_AND_LESS_THAN':
            return `>= ${Number(criteria.min).toLocaleString('id-ID')} dan < ${Number(criteria.max).toLocaleString('id-ID')}`;
          case 'GREATER_THAN_AND_LESS_THAN_OR_EQUAL':
            return `> ${Number(criteria.min).toLocaleString('id-ID')} dan <= ${Number(criteria.max).toLocaleString('id-ID')}`;
          case 'GREATER_THAN_OR_EQUAL_AND_LESS_THAN_OR_EQUAL':
            return `>= ${Number(criteria.min).toLocaleString('id-ID')} dan <= ${Number(criteria.max).toLocaleString('id-ID')}`;
          default:
            return '-';
        }
      },
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (record: any) => (
        <Button danger size="small" onClick={() => removeGoodMetricCriteria(record.metricKey)}>
          Hapus
        </Button>
      ),
    },
    {
      title: 'Override Deskripsi',
      dataIndex: 'criteria',
      key: 'descriptionOverride',
      render: (criteria: any) => criteria?.descriptionOverride || '-',
    },
  ];

  return (
    <div>
      <ContentWithErrorHandler isError={isError} isLoading={isFetching} withBox={true}>
        <Tabs defaultActiveKey="anggota">
          <TabPane tab="Anggota" key="anggota">
            <Formik
              initialValues={initialData}
              onSubmit={async (values, actions) => {
                const hide = message.loading('Loading', 0);
                await saveBasicConfig(values, hide);
                actions.setSubmitting(false);
              }}
              enableReinitialize={true}
              render={() => (
                <Form layout="vertical">
                  <Form.Item name="countEntrepreneur" label={`Jumlah anggota wirausaha`}>
                    <Input name="countEntrepreneur" addonAfter="orang" placeholder={`Masukkan jumlah anggota wirausaha`} />
                  </Form.Item>
                  <SubmitButton>Simpan</SubmitButton>
                </Form>
              )}
            />
          </TabPane>

          <TabPane tab="Input Keuangan" key="input-keuangan">
            <Formik
              initialValues={initialGeneralData}
              onSubmit={async (values, actions) => {
                const hide = message.loading('Loading', 0);
                await saveGeneralDataConfig(values, hide);
                actions.setSubmitting(false);
              }}
              enableReinitialize={true}
              render={() => (
                <Form layout="vertical">
                  <Form.Item name="month" label="Bulan">
                    <Select name="month" placeholder="Pilih bulan">
                      <Option value={1}>{monthLabelMap[1]}</Option>
                      <Option value={2}>{monthLabelMap[2]}</Option>
                      <Option value={3}>{monthLabelMap[3]}</Option>
                      <Option value={4}>{monthLabelMap[4]}</Option>
                      <Option value={5}>{monthLabelMap[5]}</Option>
                      <Option value={6}>{monthLabelMap[6]}</Option>
                      <Option value={7}>{monthLabelMap[7]}</Option>
                      <Option value={8}>{monthLabelMap[8]}</Option>
                      <Option value={9}>{monthLabelMap[9]}</Option>
                      <Option value={10}>{monthLabelMap[10]}</Option>
                      <Option value={11}>{monthLabelMap[11]}</Option>
                      <Option value={12}>{monthLabelMap[12]}</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="year" label="Tahun">
                    <InputNumber name="year" placeholder="Masukkan tahun" min={2000} max={2100} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="nplLessThanOneYearAmount" label="NPL < 1 Tahun (Rupiah)">
                    <InputNumber
                      name="nplLessThanOneYearAmount"
                      placeholder="Masukkan NPL < 1 tahun"
                      min={0}
                      formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/Rp\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item name="nplMoreThanOneYearAmount" label="NPL > 1 Tahun (Rupiah)">
                    <InputNumber
                      name="nplMoreThanOneYearAmount"
                      placeholder="Masukkan NPL > 1 tahun"
                      min={0}
                      formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/Rp\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item name="chargedOffLoanAmount" label="Charged Off Loan (Rupiah)">
                    <InputNumber
                      name="chargedOffLoanAmount"
                      placeholder="Masukkan charged off loan"
                      min={0}
                      formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/Rp\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item name="accumulatedRecoveryAmount" label="Akumulasi Recovery (Rupiah)">
                    <InputNumber
                      name="accumulatedRecoveryAmount"
                      placeholder="Masukkan akumulasi recovery"
                      min={0}
                      formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/Rp\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <SubmitButton>Simpan</SubmitButton>
                </Form>
              )}
            />
            <Divider />
            <Table
              columns={columns}
              dataSource={[...generalReportDataList]
                .sort((left, right) => {
                  if (left.year !== right.year) return right.year - left.year;
                  return right.month - left.month;
                })
                .map((item, index) => ({ ...item, key: `${item.year}-${item.month}-${index}` }))}
              size="small"
              pagination={{ pageSize: 5 }}
            />
          </TabPane>

          <TabPane tab="Pearls" key="pearls">
            <Formik
              initialValues={{
                metricKey: lksbMetricOptions[0].key,
                type: 'GREATER_THAN' as LksbGoodMetricCriteriaType,
                value: 0,
                min: 0,
                max: 0,
                descriptionOverride: '',
              }}
              onSubmit={async (values: GoodMetricCriteriaForm, actions) => {
                if (!values.metricKey) {
                  message.error('Metrik wajib dipilih!');
                  actions.setSubmitting(false);
                  return;
                }

                let nextCriteria: any = null;
                const descriptionOverride = `${values.descriptionOverride ?? ''}`.trim();
                const isSingleValueType = ['GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL'].includes(values.type);
                const isRangeType = [
                  'GREATER_THAN_AND_LESS_THAN',
                  'GREATER_THAN_OR_EQUAL_AND_LESS_THAN',
                  'GREATER_THAN_AND_LESS_THAN_OR_EQUAL',
                  'GREATER_THAN_OR_EQUAL_AND_LESS_THAN_OR_EQUAL',
                ].includes(values.type);

                if (isSingleValueType) {
                  const threshold = Number(values.value);
                  if (!Number.isFinite(threshold)) {
                    message.error('Nilai x wajib diisi!');
                    actions.setSubmitting(false);
                    return;
                  }
                  nextCriteria = {
                    type: values.type,
                    value: threshold,
                    descriptionOverride: descriptionOverride || undefined,
                  };
                } else if (isRangeType) {
                  const min = Number(values.min);
                  const max = Number(values.max);
                  if (!Number.isFinite(min) || !Number.isFinite(max)) {
                    message.error('Nilai x dan y wajib diisi!');
                    actions.setSubmitting(false);
                    return;
                  }
                  if (min >= max) {
                    message.error('Nilai y harus lebih besar dari x!');
                    actions.setSubmitting(false);
                    return;
                  }
                  nextCriteria = {
                    type: values.type,
                    min,
                    max,
                    descriptionOverride: descriptionOverride || undefined,
                  };
                }

                const hide = message.loading('Loading', 0);
                const updatedMap = {
                  ...goodMetricCriteriaMap,
                  [values.metricKey]: nextCriteria,
                };

                await saveGoodMetricCriteriaConfig(updatedMap, hide);
                actions.setSubmitting(false);
              }}
              render={({ values }) => (
                <Form layout="vertical">
                  <Form.Item name="metricKey" label="Metrik">
                    <Select name="metricKey" placeholder="Pilih metrik">
                      {lksbMetricOptions.map((metric) => (
                        <Option key={metric.key} value={metric.key}>
                          {metric.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="type" label="Jenis Kriteria">
                    <Select name="type" placeholder="Pilih jenis kriteria">
                      {Object.keys(goodMetricTypeLabelMap).map((type) => (
                        <Option key={type} value={type}>
                          {goodMetricTypeLabelMap[type as LksbGoodMetricCriteriaType]}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {(values as GoodMetricCriteriaForm).type !== 'GREATER_THAN_AND_LESS_THAN' &&
                  (values as GoodMetricCriteriaForm).type !== 'GREATER_THAN_OR_EQUAL_AND_LESS_THAN' &&
                  (values as GoodMetricCriteriaForm).type !== 'GREATER_THAN_AND_LESS_THAN_OR_EQUAL' &&
                  (values as GoodMetricCriteriaForm).type !== 'GREATER_THAN_OR_EQUAL_AND_LESS_THAN_OR_EQUAL' ? (
                    <Form.Item name="value" label="Nilai x">
                      <InputNumber name="value" placeholder="Masukkan nilai x" style={{ width: '100%' }} />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item name="min" label="Nilai x (batas bawah)">
                        <InputNumber name="min" placeholder="Masukkan nilai x" style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="max" label="Nilai y (batas atas)">
                        <InputNumber name="max" placeholder="Masukkan nilai y" style={{ width: '100%' }} />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item name="descriptionOverride" label="Override Deskripsi (Opsional)">
                    <Input name="descriptionOverride" placeholder="Masukkan deskripsi metrik pengganti" />
                  </Form.Item>
                  <SubmitButton>Simpan Kriteria</SubmitButton>
                </Form>
              )}
            />
            <Divider />
            <Table
              columns={goodMetricColumns}
              dataSource={Object.keys(goodMetricCriteriaMap)
                .map((metricKey) => ({ key: metricKey, metricKey, criteria: goodMetricCriteriaMap[metricKey] }))
                .sort((a, b) => a.metricKey.localeCompare(b.metricKey))}
              size="small"
              pagination={{ pageSize: 5 }}
            />
          </TabPane>
        </Tabs>
      </ContentWithErrorHandler>
    </div>
  );
}

export default GeneralInfoConfig;
